import { ACTIVE_VEHICLE_TYPES } from '@/config/vehicleTypes';
import {
  LOCATION_TYPE_OPTIONS,
  ROAD_CONFIGURATION_OPTIONS,
  ROAD_SURFACE_OPTIONS,
  WEATHER_OPTIONS,
  labelFor,
} from '@/config/surveyOptions';
import type { IntervalCounts, Survey } from '@/types/survey';
import { elapsedSeconds, formatDuration } from '@/utils/duration';
import { formatClockTime, sanitizeFilenamePart } from '@/utils/format';
import { totalCount } from '@/utils/counts';
import { formatIntervalRange } from '@/utils/intervals';

/**
 * Local TXT export. Generated entirely in the browser with the Blob API so it
 * works with no network and no backend.
 */

const RULE = '================================';
const THIN_RULE = '--------------------------------';

function line(label: string, value: string | number | undefined | null): string {
  const text = value === undefined || value === null || value === '' ? '-' : String(value);
  return `${label}: ${text}`;
}

/**
 * Builds the 15 minute breakdown block: one column per vehicle code, one row
 * per clock-aligned period, plus a TOTAL row that must equal the grand total.
 * Written as fixed-width columns so it lines up in Notepad.
 */
function buildIntervalBlock(intervals: IntervalCounts[]): string[] {
  if (intervals.length === 0) return ['(no interval data recorded)'];

  const codes = ACTIVE_VEHICLE_TYPES.map((t) => t.code);
  const width = Math.max(5, ...codes.map((c) => c.length));
  const cell = (value: string) => value.padStart(width);

  const header = ['PERIOD'.padEnd(15), ...codes.map(cell), cell('TOTAL')].join(' ');
  const lines = [header, '-'.repeat(header.length)];

  for (const interval of intervals) {
    lines.push(
      [
        formatIntervalRange(interval.start, interval.end).padEnd(15),
        ...ACTIVE_VEHICLE_TYPES.map((t) => cell(String(interval.counts[t.id] ?? 0))),
        cell(String(interval.total)),
      ].join(' '),
    );
  }

  const totals = ACTIVE_VEHICLE_TYPES.map((t) =>
    intervals.reduce((sum, i) => sum + (i.counts[t.id] ?? 0), 0),
  );
  lines.push('-'.repeat(header.length));
  lines.push(
    [
      'TOTAL'.padEnd(15),
      ...totals.map((n) => cell(String(n))),
      cell(String(totals.reduce((a, b) => a + b, 0))),
    ].join(' '),
  );

  return lines;
}

/** The complete human-readable survey record. */
export function buildSurveyText(survey: Survey, intervals: IntervalCounts[] = []): string {
  const duration = elapsedSeconds(survey.actualStartTimestamp, survey.actualEndTimestamp);
  const counts = survey.vehicleCounts;
  const total = totalCount(counts);

  const rows = ACTIVE_VEHICLE_TYPES.map((type) => {
    const axles = type.axleConfig ? ` [${type.axleConfig}]` : '';
    return `${type.code} - ${type.name}${axles}: ${counts[type.id] ?? 0}`;
  });

  const sections: string[] = [
    'TRAFFIC COUNT SURVEY',
    RULE,
    '',
    line('Survey ID', survey.id),
    line('Surveyor', survey.surveyorName),
    line('Date', survey.surveyDate),
    line('Planned Start', survey.plannedStartTime),
    line('Start Time', formatClockTime(survey.actualStartTimestamp)),
    line('End Time', formatClockTime(survey.actualEndTimestamp)),
    line('Duration', formatDuration(duration)),
    '',
    'LOCATION',
    RULE,
    line('Latitude', survey.latitude !== undefined ? survey.latitude.toFixed(6) : null),
    line('Longitude', survey.longitude !== undefined ? survey.longitude.toFixed(6) : null),
    line(
      'GPS Accuracy',
      survey.gpsAccuracy !== undefined ? `${Math.round(survey.gpsAccuracy)} m` : null,
    ),
    line('District', survey.district),
    line('Location Note', survey.manualLocation),
    '',
    'ROAD INFORMATION',
    RULE,
    line('Road Number', survey.roadNumber),
    line('MCC No', survey.mccNumber),
    line('Weather', labelFor(WEATHER_OPTIONS, survey.weather)),
    line('Location Type', labelFor(LOCATION_TYPE_OPTIONS, survey.locationType)),
    line('Road Surface', labelFor(ROAD_SURFACE_OPTIONS, survey.roadSurface)),
    line('Road Configuration', labelFor(ROAD_CONFIGURATION_OPTIONS, survey.roadConfiguration)),
    line('Direction', survey.direction),
    line('No. of Lanes', survey.laneCount),
    '',
    'VEHICLE COUNTS (WHOLE SURVEY)',
    RULE,
    ...rows,
    THIN_RULE,
    `TOTAL: ${total}`,
    '',
    'BREAKDOWN BY 15 MINUTE PERIOD (CLOCK TIME)',
    RULE,
    ...buildIntervalBlock(intervals),
    '',
    'COMMENTS',
    RULE,
    survey.comments?.trim() ? survey.comments.trim() : '(none)',
    '',
    THIN_RULE,
    line('App Version', survey.appVersion),
    line('Recorded On Device At', new Date(survey.createdAt).toString()),
    '',
  ];

  // CRLF keeps Windows Notepad happy, which is where these files usually land.
  return sections.join('\r\n');
}

/** e.g. "TrafficSurvey_A01_2026-09-18_0730.txt" */
export function buildFileName(survey: Survey): string {
  const road = sanitizeFilenamePart(survey.roadNumber, 'road');
  const date = sanitizeFilenamePart(survey.surveyDate, 'date');
  const time = sanitizeFilenamePart(survey.plannedStartTime, '0000').replace('-', '');
  return `TrafficSurvey_${road}_${date}_${time}.txt`;
}

/**
 * Triggers the browser download. Returns the filename on success.
 *
 * Throws on failure so the caller can mark the "Local File" channel as failed
 * without affecting the database or Telegram channels.
 */
export function downloadSurveyText(survey: Survey, intervals: IntervalCounts[] = []): string {
  const text = buildSurveyText(survey, intervals);
  const filename = buildFileName(survey);
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Give the browser a moment to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  return filename;
}
